<template>
  <section>
    <h1>{{ $t("user-profile-title") }}</h1>
    <form>
      <div class="form-group">
        <router-link :to="{ name: 'dashboard' }" class="back-link">
          <long-arrow width="16" height="18" direction="left"></long-arrow>
          {{ $t("back-to-dashboard") }}
        </router-link>
      </div>
      <div class="form-group">
        <h2>{{ $t("profile-account-details") }}</h2>
      </div>
      <div class="form-group">
        <h3>{{ $t("profile-registered-email") }}</h3>
        <h3 class="flex">
          <user-icon
            width="16"
            height="16"
            weight="solid"
            fillColor="#7e45ff"
            cssClass="user-icon"
          ></user-icon>
          {{ user }}
        </h3>
      </div>
      <fieldset>
        <h3>{{ $t("profile-change-password") }}</h3>
        <p v-if="passwordResetted" class="error-description">
          {{ $t("user-profile-password-changed") }}
        </p>
        <div class="form-group">
          <label>
            {{ $t("profile-current-password") }}
            <input
              type="password"
              name="password"
              v-model.trim="oldPassword"
              autocomplete="oldPassword"
              :placeholder="$t('profile-current-password')"
            />
          </label>
        </div>
        <div class="form-group">
          <label>
            {{ $t("auth-new-password") }}
            <input
              type="password"
              name="password"
              v-model.trim="newPassword"
              autocomplete="newPassword"
              :placeholder="$t('auth-new-password')"
            />
          </label>
        </div>
        <div class="form-group">
          <label>
            {{ $t("auth-confirm-new-password") }}
            <input
              type="password"
              name="confirmPassword"
              v-model.trim="confirmPassword"
              autocomplete="confirm-new-password"
              :placeholder="$t('auth-confirm-new-password')"
            />
          </label>
          <!-- TODO move meter to separate component as it is used in two places-->
          <div id="meter">
            <div id="password-strength-description">
              <p>{{ $t("auth-form-register-password-strength-title") }}</p>
              <p v-if="strength" :class="strength">
                {{ $t(`auth-form-register-password-strength-${strength}`) }}
              </p>
            </div>
            <div id="password-strength-meter">
              <span :data-length="passwordStrengthMeter"></span>
            </div>
          </div>
        </div>
      </fieldset>
      <div class="form-group flex">
        <button
          type="submit"
          class="primary"
          @click.prevent="changePassword"
          :disabled="
            !oldPassword ||
              !newPassword ||
              !confirmPassword ||
              newPassword !== confirmPassword ||
              passwordStrengthMeter < 1
          "
        >
          {{ $t("profile-change-password") }}
        </button>
      </div>
    </form>
  </section>
</template>

<script>
import { mapState, mapActions } from "vuex";
import UserIcon from "@/components/icons/UserIcon";
import LongArrow from "@/components/icons/LongArrowIcon";
import zxcvbn from "zxcvbn";

export default {
  name: "Profile",
  components: {
    LongArrow,
    UserIcon
  },
  data() {
    return {
      passwordResetted: false,
      oldPassword: "",
      newPassword: "",
      confirmPassword: ""
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    passwordStrengthMeter() {
      const strength = zxcvbn(this.newPassword);
      return strength.score;
    },
    strength() {
      let strength = "";
      switch (this.passwordStrengthMeter) {
        case 1:
          strength = "weak";
          break;
        case 2:
          strength = "medium";
          break;
        case 3:
          strength = "strong";
          break;
        case 4:
          strength = "strong";
          break;
      }
      return strength;
    }
  },
  methods: {
    ...mapActions("common", ["setLoading", "showModal"]),
    changePassword() {
      this.setLoading(true);
      const data = {
        oldPassword: this.oldPassword,
        newPassword: this.newPassword
      };
      this.$store
        .dispatch("auth/setNewPassword", data)
        .then(() => {
          this.setLoading(false);
          this.passwordResetted = true;
          this.resetForm();
        })
        .catch(err => {
          this.passwordResetted = false;
          this.setLoading(false);
          this.showModal({
            title: err.errorCode
          });
          this.resetForm();
        });
    },
    resetForm() {
      this.oldPassword = "";
      this.newPassword = "";
      this.confirmPassword = "";
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../assets/styles/variables";

section {
  flex: 2;
  padding: 20px;
  overflow: auto;
  height: calc(100vh - 87px);
  min-height: 713px;
}

.back-link {
  display: flex;
  text-decoration: none;
  padding: 20px 0;
  svg {
    margin-right: 10px;
  }
}

.user-icon {
  margin-right: 5px;
}

form {
  max-width: 600px;
  label {
    font-size: 14px;
  }
}

fieldset {
  margin-top: 40px;
}

#meter {
  margin: 10px 0;
}
#password-strength-description {
  display: flex;
  justify-content: space-between;
  > p {
    margin: 0;
    padding: 5px 0;
    color: $placeholder-color;
    &.weak {
      color: $error-color;
    }
    &.medium {
      color: $warning-color;
    }
    &.strong {
      color: $success-color;
    }
  }
}
#password-strength-meter {
  width: 100%;
  height: 12px;
  border-radius: $radius;
  background-color: $middle-gray;
  > span {
    transition: width 0.5s;
    display: block;
    height: 100%;
    border-radius: $radius;
    width: 0;
    &[data-length="1"] {
      width: 33%;
      background-color: $error-color;
    }
    &[data-length="2"] {
      width: 66%;
      background-color: $warning-color;
    }
    &[data-length="3"] {
      width: 100%;
      background-color: $success-color;
    }
    &[data-length="4"] {
      width: 100%;
      background-color: $success-color;
    }
  }
}
</style>
